<template>
    <div class="integration-form">
        <b-card>
            <h2>{{ id ? $t('coupon.edit') : $t('coupon.add') }}</h2>

            <b-form @submit.prevent="handleSubmit" @reset="resetForm" novalidate>
                <div class="mb-3 text-left">
                    <label class="mb-1" for="code">{{ $t('coupon.code') }}</label>
                    <b-form-input v-model="formFields.code" :placeholder="$t('coupon.code')" />
                    <b-form-invalid-feedback :state="!formErrors.has('code')">
                        {{ formErrors.first('code') }}
                    </b-form-invalid-feedback>
                </div>

                <div class="mb-3 text-left">
                    <label class="mb-1" for="type">{{ $t('coupon.type') }}</label>
                    <b-form-select v-model="formFields.type" :options="typeOptions" />
                    <b-form-invalid-feedback :state="!formErrors.has('type')">
                        {{ formErrors.first('type') }}
                    </b-form-invalid-feedback>
                </div>

                <div class="mb-3 text-left">
                    <label class="mb-1" for="value">{{ $t('coupon.value') }}</label>
                    <b-form-input type="number" v-model="formFields.value" />
                    <b-form-invalid-feedback :state="!formErrors.has('value')">
                        {{ formErrors.first('value') }}
                    </b-form-invalid-feedback>
                </div>

                <div class="mb-3 text-left">
                    <label class="mb-1" for="max_discount">{{ $t('coupon.max_discount') }}</label>
                    <b-form-input type="number" v-model="formFields.max_discount" />
                    <b-form-invalid-feedback :state="!formErrors.has('max_discount')">
                        {{ formErrors.first('max_discount') }}
                    </b-form-invalid-feedback>
                </div>

                <div class="mb-3 text-left">
                    <label class="mb-1" for="min_order_amount">{{ $t('coupon.min_order_amount') }}</label>
                    <b-form-input type="number" v-model="formFields.min_order_amount" />
                    <b-form-invalid-feedback :state="!formErrors.has('min_order_amount')">
                        {{ formErrors.first('min_order_amount') }}
                    </b-form-invalid-feedback>
                </div>

                <b-row>
                    <b-col>
                        <label class="mb-1">{{ $t('coupon.starts_at') }}</label>
                        <b-form-datepicker v-model="formFields.starts_at_date" class="w-100" />
                        <b-form-timepicker v-model="formFields.starts_at_time" class="w-100 mt-1"
                            :show-seconds="false" />
                        <b-form-invalid-feedback :state="!formErrors.has('starts_at')">
                            {{ formErrors.first('starts_at') }}
                        </b-form-invalid-feedback>
                    </b-col>
                    <b-col>
                        <label class="mb-1">{{ $t('coupon.expires_at') }}</label>
                        <b-form-datepicker v-model="formFields.expires_at_date" class="w-100" />
                        <b-form-timepicker v-model="formFields.expires_at_time" class="w-100 mt-1"
                            :show-seconds="false" />
                        <b-form-invalid-feedback :state="!formErrors.has('expires_at')">
                            {{ formErrors.first('expires_at') }}
                        </b-form-invalid-feedback>
                    </b-col>
                </b-row>

                <b-row class="mt-3">
                    <b-col>
                        <label class="mb-1">{{ $t('coupon.usage_limit') }}</label>
                        <b-form-input type="number" v-model="formFields.usage_limit" />
                        <b-form-invalid-feedback :state="!formErrors.has('usage_limit')">
                            {{ formErrors.first('usage_limit') }}
                        </b-form-invalid-feedback>
                    </b-col>
                    <b-col>
                        <label class="mb-1">{{ $t('coupon.usage_limit_per_user') }}</label>
                        <b-form-input type="number" v-model="formFields.usage_limit_per_user" />
                        <b-form-invalid-feedback :state="!formErrors.has('usage_limit_per_user')">
                            {{ formErrors.first('usage_limit_per_user') }}
                        </b-form-invalid-feedback>
                    </b-col>
                </b-row>

                <!-- Coupon Status -->
                <div class="mb-3 text-left">
                    <label class="mb-1" for="is_active">{{ $t('coupon.status') }}</label>
                    <treeselect :placeholder="$t('coupon.status')" v-model="formFields.is_active"
                        :options="statusOptions" :normalizer="normalizeOption" class="form-input" />
                    <b-form-invalid-feedback :state="!formErrors.has('is_active')">
                        {{ formErrors.first('is_active') }}
                    </b-form-invalid-feedback>
                </div>



                <div class="d-flex gap-2 mt-4">
                    <b-button type="submit" variant="primary" :disabled="isSubmitting">
                        <b-spinner small v-if="isSubmitting" class="mr-1" />
                        {{ $t('action.submit') }}
                    </b-button>
                    <b-button variant="secondary" @click="cancel">
                        {{ $t('action.cancel') }}
                    </b-button>
                </div>
            </b-form>
        </b-card>
    </div>
</template>

<script>
import { request } from '@/Util/Request';
import Error from '@/Util/Error';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
const FORM_STATE = {
    code: '',
    type: '%',
    value: 0,
    max_discount: null,
    min_order_amount: null,
    starts_at: null,
    expires_at: null,
    usage_limit: null,
    usage_limit_per_user: null,
    is_active: 1,
    starts_at_date: null,
    starts_at_time: null,
    expires_at_date: null,
    expires_at_time: null,
};

export default {
    name: 'CouponForm',
    components: {
        Treeselect,
    },
    data() {
        return {
            id: null,
            formFields: { ...FORM_STATE },
            formErrors: new Error({}),
            isSubmitting: false,
            typeOptions: [
                { value: '%', text: '%' },
                { value: '$', text: '$' },
            ],
            statusOptions: [
                { id: 1, label: this.$t('coupon.active') },
                { id: 0, label: this.$t('coupon.inactive') }
            ],

        };
    },
    mounted() {
        this.id = this.$route.params.id || null;
        if (this.id) this.loadCoupon();
    },
    methods: {
        normalizeOption(option) {
            return {
                id: option.id,
                label: option.label,
            };
        },
        async loadCoupon() {
            try {
                const response = await request({ method: 'get', url: `/coupons/${this.id}` });
                const data = response.data;

                this.formFields = {
                    ...FORM_STATE,
                    ...data,
                    starts_at_date: data.starts_at ? data.starts_at.split(' ')[0] : null,
                    starts_at_time: data.starts_at ? data.starts_at.split(' ')[1].slice(0, 5) : null, // "HH:MM"
                    expires_at_date: data.expires_at ? data.expires_at.split(' ')[0] : null,
                    expires_at_time: data.expires_at ? data.expires_at.split(' ')[1].slice(0, 5) : null, // "HH:MM"
                };
            } catch (e) {
                console.error('Failed to load coupon', e);
            }
        },

        combineDateTime(date, time) {
            if (!date) return null;
            if (!time) return date + ' 00:00:00';
            return `${date} ${time}`;
        },
        async handleSubmit() {
            try {
                this.formErrors = new Error({});
                this.isSubmitting = true;
                this.formFields.starts_at = this.combineDateTime(this.formFields.starts_at_date, this.formFields.starts_at_time);
                this.formFields.expires_at = this.combineDateTime(this.formFields.expires_at_date, this.formFields.expires_at_time);
                const method = this.id ? 'put' : 'post';
                const url = this.id ? `/coupons/${this.id}` : '/coupons';

                await request({ method, url, data: this.formFields });

                this.$bvToast.toast(
                    this.$t(this.id ? 'coupon.updated_successfully' : 'coupon.created_successfully'),
                    {
                        title: this.$t('common.success'),
                        variant: 'success',
                        solid: true,
                    }
                );

                this.$router.push('/admin/coupon');
            } catch (error) {
                if (error.request?.status === 422) {
                    this.formErrors = new Error(JSON.parse(error.request.responseText).errors);
                    return false;
                } else {
                    this.$bvToast.toast(this.$t('common.something_went_wrong'), {
                        title: this.$t('common.error'),
                        variant: 'danger',
                        solid: true,
                    });
                }
            } finally {
                this.isSubmitting = false;
            }
        },
        cancel() {
            this.$router.back();
        },
        resetForm() {
            this.formFields = { ...FORM_STATE };
            this.formErrors = new Error({});
        },
    },
};
</script>

<style scoped>
/* Add form-specific styling here */
</style>
